<template>
    <v-dialog v-model="show" max-width="900px" scrollable>
        <v-card class="pa-4">
            <v-card-title class="d-flex justify-space-between align-center">
                <span>Solicitações de Reagendamento</span>
                <v-chip v-if="pendingRequests.length" color="secondary" small class="white--text">
                    {{ pendingRequests.length }} pendente{{ pendingRequests.length > 1 ? 's' : '' }}
                </v-chip>
            </v-card-title>
            
            <v-card-text style="max-height: 600px;">
                <v-row v-if="loading" justify="center" class="pa-8">
                    <v-col cols="auto">
                        <v-progress-circular indeterminate color="primary" />
                    </v-col>
                </v-row>

                <v-row v-else-if="!pendingRequests.length" justify="center" class="pa-8">
                    <v-col cols="12" class="text-center">
                        <v-icon size="64" color="secondary">mdi-calendar-check</v-icon>
                        <p class="text-h6 mt-4 secondary--text">Nenhuma solicitação pendente</p>
                    </v-col>
                </v-row>

                <v-expansion-panels v-else accordion>
                    <v-expansion-panel v-for="request in pendingRequests" :key="request.id">
                        <v-expansion-panel-header>
                            <v-row no-gutters align="center">
                                <v-col cols="12" md="4">
                                    <div class="d-flex align-center">
                                        <v-icon left color="primary">mdi-account</v-icon>
                                        <span class="font-weight-bold">{{ request.customers_session?.customer?.name || 'Cliente' }}</span>
                                    </div>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <div class="text-caption secondary--text">Solicitado em</div>
                                    <div>{{ formatDateTime(request.created_at) }}</div>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <v-chip small :color="getStatusColor(request.status)" class="white--text">
                                        {{ getStatusText(request.status) }}
                                    </v-chip>
                                </v-col>
                            </v-row>
                        </v-expansion-panel-header>
                        
                        <v-expansion-panel-content>
                            <v-card flat color="surface" class="pa-4 rounded-xl">
                                <v-row>
                                    <v-col cols="12" md="6">
                                        <div class="text-subtitle-2 secondary--text mb-2">Horário Atual</div>
                                        <v-card flat color="white" class="pa-3 rounded-lg">
                                            <div class="d-flex align-center">
                                                <v-icon left color="primary">mdi-calendar-clock</v-icon>
                                                <div>
                                                    <div class="font-weight-bold">{{ formatDateTime(request.customers_session?.session_date) }}</div>
                                                    <div class="text-caption secondary--text">{{ request.customers_session?.service?.name || 'Serviço' }}</div>
                                                </div>
                                            </div>
                                        </v-card>
                                    </v-col>
                                    
                                    <v-col cols="12" md="6">
                                        <div class="text-subtitle-2 secondary--text mb-2">Horário Solicitado</div>
                                        <v-card flat color="primary lighten-5" class="pa-3 rounded-lg">
                                            <div class="d-flex align-center">
                                                <v-icon left color="primary">mdi-calendar-arrow-right</v-icon>
                                                <div>
                                                    <div class="font-weight-bold primary--text">{{ formatDateTime(request.new_date + ' ' + request.new_time) }}</div>
                                                    <div class="text-caption secondary--text">Nova data solicitada</div>
                                                </div>
                                            </div>
                                        </v-card>
                                    </v-col>

                                    <v-col v-if="request.reason" cols="12">
                                        <div class="text-subtitle-2 secondary--text mb-2">Motivo</div>
                                        <v-card flat color="white" class="pa-3 rounded-lg">
                                            <p class="mb-0">{{ request.reason }}</p>
                                        </v-card>
                                    </v-col>

                                    <!-- Propose New Time Section -->
                                    <v-col v-if="proposingFor === request.id" cols="12">
                                        <v-divider class="my-4" />
                                        <div class="text-subtitle-2 secondary--text mb-3">Propor Novos Horários</div>
                                        <v-row>
                                            <v-col cols="12" md="6">
                                                <v-menu
                                                    v-model="showDatePicker"
                                                    :close-on-content-click="false"
                                                    transition="scale-transition"
                                                    offset-y
                                                    min-width="auto"
                                                >
                                                    <template v-slot:activator="{ on, attrs }">
                                                        <v-text-field
                                                            :value="formatDate(proposedDate)"
                                                            label="Data proposta"
                                                            append-icon="mdi-calendar"
                                                            readonly
                                                            v-bind="attrs"
                                                            v-on="on"
                                                        />
                                                    </template>
                                                    <v-date-picker
                                                        v-model="proposedDate"
                                                        no-title
                                                        scrollable
                                                        @input="showDatePicker = false"
                                                    />
                                                </v-menu>
                                            </v-col>
                                            <v-col cols="12" md="6">
                                                <v-menu
                                                    v-model="showTimePicker"
                                                    :close-on-content-click="false"
                                                    transition="scale-transition"
                                                    offset-y
                                                    min-width="300px"
                                                >
                                                    <template v-slot:activator="{ on, attrs }">
                                                        <v-text-field
                                                            v-model="proposedTime"
                                                            label="Hora proposta"
                                                            append-icon="mdi-clock-outline"
                                                            readonly
                                                            v-bind="attrs"
                                                            v-on="on"
                                                        />
                                                    </template>
                                                    <v-time-picker
                                                        v-if="showTimePicker"
                                                        v-model="proposedTime"
                                                        format="24hr"
                                                        full-width
                                                        @click:minute="showTimePicker = false"
                                                    />
                                                </v-menu>
                                            </v-col>
                                        </v-row>
                                    </v-col>

                                    <!-- Action Buttons -->
                                    <v-col cols="12">
                                        <v-divider class="my-2" />
                                        <v-row justify="end" class="mt-2">
                                            <v-col cols="auto">
                                                <v-btn
                                                    v-if="proposingFor === request.id"
                                                    outlined
                                                    color="secondary"
                                                    @click="cancelPropose"
                                                    :disabled="actionLoading"
                                                >
                                                    <v-icon left>mdi-close</v-icon>
                                                    Cancelar
                                                </v-btn>
                                                <v-btn
                                                    v-else
                                                    outlined
                                                    color="error"
                                                    @click="rejectRequest(request.id)"
                                                    :loading="actionLoading && currentAction === 'reject-' + request.id"
                                                    :disabled="actionLoading"
                                                >
                                                    <v-icon left>mdi-close-circle</v-icon>
                                                    Rejeitar
                                                </v-btn>
                                            </v-col>
                                            <v-col cols="auto">
                                                <v-btn
                                                    v-if="proposingFor === request.id"
                                                    color="primary"
                                                    @click="submitProposal(request.id)"
                                                    :loading="actionLoading && currentAction === 'propose-' + request.id"
                                                    :disabled="actionLoading || !proposedDate || !proposedTime"
                                                >
                                                    <v-icon left>mdi-send</v-icon>
                                                    Enviar Proposta
                                                </v-btn>
                                                <v-btn
                                                    v-else
                                                    outlined
                                                    color="primary"
                                                    @click="startPropose(request.id)"
                                                    :disabled="actionLoading"
                                                >
                                                    <v-icon left>mdi-calendar-edit</v-icon>
                                                    Propor Novo Horário
                                                </v-btn>
                                            </v-col>
                                            <v-col v-if="proposingFor !== request.id" cols="auto">
                                                <v-btn
                                                    color="success"
                                                    @click="approveRequest(request.id)"
                                                    :loading="actionLoading && currentAction === 'approve-' + request.id"
                                                    :disabled="actionLoading"
                                                >
                                                    <v-icon left>mdi-check-circle</v-icon>
                                                    Aprovar
                                                </v-btn>
                                            </v-col>
                                        </v-row>
                                    </v-col>
                                </v-row>
                            </v-card>
                        </v-expansion-panel-content>
                    </v-expansion-panel>
                </v-expansion-panels>
            </v-card-text>

            <v-card-actions>
                <v-spacer />
                <v-btn color="secondary" outlined @click="close">
                    <v-icon left>mdi-close</v-icon>
                    Fechar
                </v-btn>
            </v-card-actions>
        </v-card>

        <!-- Success Snackbar -->
        <v-snackbar v-model="showSuccess" color="success" timeout="3000" top>
            {{ successMessage }}
            <template v-slot:action="{ attrs }">
                <v-btn text v-bind="attrs" @click="showSuccess = false">Fechar</v-btn>
            </template>
        </v-snackbar>

        <!-- Error Snackbar -->
        <v-snackbar v-model="showError" color="error" timeout="5000" top>
            {{ errorMessage }}
            <template v-slot:action="{ attrs }">
                <v-btn text v-bind="attrs" @click="showError = false">Fechar</v-btn>
            </template>
        </v-snackbar>
    </v-dialog>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import dayjs from 'dayjs';

export default {
    props: {
        value: {
            type: Boolean,
            required: false,
            default: false
        },
        customerId: {
            type: Number,
            required: false,
            default: null
        }
    },
    data() {
        return {
            show: this.value,
            loading: false,
            actionLoading: false,
            currentAction: null,
            proposingFor: null,
            proposedDate: null,
            proposedTime: null,
            showDatePicker: false,
            showTimePicker: false,
            showSuccess: false,
            showError: false,
            successMessage: '',
            errorMessage: ''
        };
    },
    computed: {
        ...mapState("schedule", {
            allRequests: state => state.reschedule_requests
        }),
        pendingRequests() {
            // Ensure allRequests is an array
            const requestsData = Array.isArray(this.allRequests) 
                ? this.allRequests 
                : (this.allRequests?.data || []);
            
            let requests = requestsData;
            
            // Filter by customer if customerId is provided
            if (this.customerId) {
                requests = requests.filter(r => r.customers_session.customer_id === this.customerId);
            }
            
            // Only show pending requests
            return requests.filter(r => r.status === 'pending');
        }
    },
    watch: {
        value() {
            this.show = this.value;
            if (this.show) {
                this.loadRequests();
            }
        },
        show() {
            this.$emit("input", this.show);
            if (!this.show) {
                this.resetPropose();
            }
        }
    },
    mounted() {
        if (this.show) {
            this.loadRequests();
        }
    },
    methods: {
        ...mapActions("schedule", [
            "ActionDoGetRescheduleRequests",
            "ActionDoApproveRescheduleRequest",
            "ActionDoRejectRescheduleRequest",
            "ActionDoProposeRescheduleRequest",
            "ActionDoGetCalendarEvents"
        ]),
        loadRequests() {
            this.loading = true;
            this.ActionDoGetRescheduleRequests()
                .catch(error => {
                    console.error('Error loading reschedule requests:', error);
                    this.showErrorMessage('Erro ao carregar solicitações de reagendamento');
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        approveRequest(requestId) {
            this.currentAction = 'approve-' + requestId;
            this.actionLoading = true;
            
            this.ActionDoApproveRescheduleRequest(requestId)
                .then(() => {
                    this.showSuccessMessage('Solicitação aprovada com sucesso!');
                    this.loadRequests();
                    this.ActionDoGetCalendarEvents();
                    this.$emit('requestUpdated');
                })
                .catch(error => {
                    console.error('Error approving request:', error);
                    this.showErrorMessage('Erro ao aprovar solicitação');
                })
                .finally(() => {
                    this.actionLoading = false;
                    this.currentAction = null;
                });
        },
        rejectRequest(requestId) {
            this.currentAction = 'reject-' + requestId;
            this.actionLoading = true;
            
            this.ActionDoRejectRescheduleRequest(requestId)
                .then(() => {
                    this.showSuccessMessage('Solicitação rejeitada');
                    this.loadRequests();
                    this.$emit('requestUpdated');
                })
                .catch(error => {
                    console.error('Error rejecting request:', error);
                    this.showErrorMessage('Erro ao rejeitar solicitação');
                })
                .finally(() => {
                    this.actionLoading = false;
                    this.currentAction = null;
                });
        },
        startPropose(requestId) {
            this.proposingFor = requestId;
            this.proposedDate = null;
            this.proposedTime = null;
        },
        cancelPropose() {
            this.resetPropose();
        },
        resetPropose() {
            this.proposingFor = null;
            this.proposedDate = null;
            this.proposedTime = null;
            this.showDatePicker = false;
            this.showTimePicker = false;
        },
        submitProposal(requestId) {
            if (!this.proposedDate || !this.proposedTime) {
                this.showErrorMessage('Por favor, selecione data e hora');
                return;
            }

            this.currentAction = 'propose-' + requestId;
            this.actionLoading = true;
            
            this.ActionDoProposeRescheduleRequest({
                requestId: requestId,
                proposed_date: this.proposedDate,
                proposed_time: this.proposedTime
            })
                .then(() => {
                    this.showSuccessMessage('Proposta enviada com sucesso!');
                    this.loadRequests();
                    this.resetPropose();
                    this.$emit('requestUpdated');
                })
                .catch(error => {
                    console.error('Error proposing new time:', error);
                    this.showErrorMessage('Erro ao enviar proposta');
                })
                .finally(() => {
                    this.actionLoading = false;
                    this.currentAction = null;
                });
        },
        close() {
            this.show = false;
        },
        formatDate(date) {
            return date ? dayjs(date).format('DD/MM/YYYY') : '';
        },
        formatDateTime(datetime) {
            return datetime ? dayjs(datetime).format('DD/MM/YYYY HH:mm') : '';
        },
        getStatusColor(status) {
            const colors = {
                'pending': 'secondary-yellow',
                'approved': 'success',
                'rejected': 'error',
                'proposed': 'primary'
            };
            return colors[status] || 'secondary';
        },
        getStatusText(status) {
            const texts = {
                'pending': 'Pendente',
                'approved': 'Aprovado',
                'rejected': 'Rejeitado',
                'proposed': 'Proposta Enviada'
            };
            return texts[status] || status;
        },
        showSuccessMessage(message) {
            this.successMessage = message;
            this.showSuccess = true;
        },
        showErrorMessage(message) {
            this.errorMessage = message;
            this.showError = true;
        }
    }
};
</script>
